/**
 * ClientCard — shared portfolio tile component
 *
 * Renders one client tile with responsive AVIF + WebP + JPG thumbnail (or
 * a gradient + initial fallback when no thumbnail is configured). Used by
 * the home page's `SelectedWork` strip and the About page's Selected Work
 * grid. Client data is sourced from `@/data/clients`.
 */

import React from "react";
import { motion } from "framer-motion";
import type { ClientTile } from "@/data/clients";

interface ClientCardProps {
  tile: ClientTile;
  index: number;
  language?: "en" | "es";
}

const ClientCard: React.FC<ClientCardProps> = ({
  tile,
  index,
  language = "en",
}) => {
  const es = language === "es";
  const category = (es && tile.categoryEs) || tile.category;
  const scope = (es && tile.scopeEs) || tile.scope;
  const outcome = (es && tile.outcomeEs) || tile.outcome;

  return (
  <motion.a
    href={tile.href}
    target="_blank"
    rel="noopener noreferrer"
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{
      duration: 0.5,
      delay: (index % 3) * 0.05,
      ease: [0.16, 1, 0.3, 1],
    }}
    className="block bg-dark-950 transition-colors hover:bg-dark-900/80 group"
  >
    {/* Thumbnail */}
    <div className="relative overflow-hidden aspect-[16/9] bg-dark-800">
      {tile.thumbnail ? (
        <picture>
          <source
            type="image/avif"
            srcSet={`/images/${tile.thumbnail.folder}/${tile.thumbnail.family}-400.avif 400w, /images/${tile.thumbnail.folder}/${tile.thumbnail.family}-800.avif 800w, /images/${tile.thumbnail.folder}/${tile.thumbnail.family}-1200.avif 1200w`}
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          />
          <source
            type="image/webp"
            srcSet={`/images/${tile.thumbnail.folder}/${tile.thumbnail.family}-400.webp 400w, /images/${tile.thumbnail.folder}/${tile.thumbnail.family}-800.webp 800w, /images/${tile.thumbnail.folder}/${tile.thumbnail.family}-1200.webp 1200w`}
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          />
          <img
            src={`/images/${tile.thumbnail.folder}/${tile.thumbnail.family}-800.jpg`}
            srcSet={`/images/${tile.thumbnail.folder}/${tile.thumbnail.family}-400.jpg 400w, /images/${tile.thumbnail.folder}/${tile.thumbnail.family}-800.jpg 800w, /images/${tile.thumbnail.folder}/${tile.thumbnail.family}-1200.jpg 1200w`}
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            alt={(es && tile.thumbnail.altEs) || tile.thumbnail.alt}
            width={tile.thumbnail.width}
            height={tile.thumbnail.height}
            loading="lazy"
            decoding="async"
            style={tile.thumbnail.objectPosition ? { objectPosition: tile.thumbnail.objectPosition } : undefined}
            className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105"
          />
        </picture>
      ) : tile.fallback ? (
        <div
          className={`flex items-center justify-center w-full h-full bg-gradient-to-br ${tile.fallback.gradient}`}
        >
          <span className="font-display font-bold text-7xl text-dark-50/30 select-none">
            {tile.fallback.initial}
          </span>
        </div>
      ) : null}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-dark-950/40 to-transparent" />
    </div>

    {/* Body */}
    <div className="p-6 lg:p-7">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-xs tracking-widest text-metro-orange-500/80">
          {String(index + 1).padStart(2, "0")} · {category.toUpperCase()}
        </div>
      </div>
      <h3 className="mb-3 text-xl font-bold leading-tight font-display text-dark-50 transition-colors group-hover:text-metro-orange-300 lg:text-2xl">
        {tile.client}
      </h3>
      <p className="mb-4 text-sm leading-relaxed text-dark-300">{scope}</p>
      <p className="mb-5 text-sm font-medium leading-snug text-metro-orange-300">
        {outcome}
      </p>
      <div className="inline-flex items-center text-sm font-medium text-metro-orange-400">
        {tile.display}
        <svg
          className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 8l4 4m0 0l-4 4m4-4H3"
          />
        </svg>
      </div>
    </div>
  </motion.a>
  );
};

export default ClientCard;
